/**
 * Data layer for `fraud-blacklist-synchronization-across-vendors`. Thin typed fetch wrappers over /api/v1/fraud-blacklist-synchronization-across-vendors.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface FraudBlacklistSynchronizationAcrossVendorsRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListFraudBlacklistSynchronizationAcrossVendorsResponse {
	count: number;
	items: FraudBlacklistSynchronizationAcrossVendorsRow[];
}

export interface CreateFraudBlacklistSynchronizationAcrossVendorsBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/fraud-blacklist-synchronization-across-vendors";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListFraudBlacklistSynchronizationAcrossVendorsResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListFraudBlacklistSynchronizationAcrossVendorsResponse>(res);
}

export async function create(body: CreateFraudBlacklistSynchronizationAcrossVendorsBody): Promise<FraudBlacklistSynchronizationAcrossVendorsRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<FraudBlacklistSynchronizationAcrossVendorsRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const fraudBlacklistSynchronizationAcrossVendorsQueryKeys = {
	all: ["fraud-blacklist-synchronization-across-vendors"] as const,
	list: () => [...fraudBlacklistSynchronizationAcrossVendorsQueryKeys.all] as const,
	detail: (id: string) => [...fraudBlacklistSynchronizationAcrossVendorsQueryKeys.all, id] as const,
};
